import React, { Component } from "react";

import InputGroup from "components/InputGroup";
import CountrySelector from "components/CountrySelector";
import ButtonSelect from "components/ButtonSelect";
import Accordion from "../Accordion";
import AccordionElement from "../Accordion/element";
import ContentSwitcher from "../ContentSwitcher";
import ContentSwitcherElement from "../ContentSwitcher/element";
import {
  IconKeepWhatYouRaise,
  IconPracticeWhatYouPreach,
  IconGiveDonorsAChoice,
  IconKeepUsHonest,
  IconStayFocused,
} from "../Icon";
import { GraphFundingNow, GraphFundingFuture } from "./graphs";

import FeeStructureDonationForm from "./feeStructureDonationForm";

import "./_style.scss";

import { paymentModelButtons, euCountryCodes } from "../../helpers/constants";

const isAustralia = (country) => country === "Australia" || country === "AU";
const defaultsToSharedModel = (country) => euCountryCodes.includes(country) || isAustralia(country);

class Fees extends Component {
  constructor(props) {
    super(props);

    // Defensive fallback: environments without a real page load (Storybook, tests that don't
    // pass every prop) never populate window.Chuffed at all, unlike the real Blade-rendered page.
    const chuffed = window.Chuffed || {};
    let countries = [];

    if (typeof props.countryFees === "undefined") {
      countries = chuffed.fees;
    } else {
      countries = props.countryFees;
    }

    if (typeof props.defaultCountryCode === "undefined") {
      this.state = {
        countryFees: countries,
        defaultCountryCode: chuffed.defaultCountryCode,
        country: chuffed.defaultCountryCode,
        selectedCountryFee: countries.filter((country) => country.country_code === chuffed.defaultCountryCode),
      };
    } else {
      this.state = {
        countryFees: countries,
        country: props.defaultCountryCode,
        selectedCountryFee: countries.filter((country) => country.country_code === props.defaultCountryCode),
      };
    }
  }

  state = {
    country: "Australia",
    paymentModel: "Keep 100",
    countryFees: [],
    selectedCountryFee: [],
    defaultCountryCode: "",
    country_symbol: "$",
  };

  onCountryChange = (e) => {
    const fee = this.state.countryFees;
    const countryCode = e.value;

    this.removalKeep100Model(countryCode);

    this.setState({
      country: countryCode,
      selectedCountryFee: fee.filter((country) => country.country_code === countryCode),
    });
  };

  onPaymentFeeChange = (e) => {
    this.setState({
      paymentModel: e.target.value,
    });
  };

  removalKeep100Model = (country) => {
    this.setState({
      paymentModel: defaultsToSharedModel(country) ? "Shared" : "Keep 100",
    });
  };

  render() {
    // The constructor's `this.state = {...}` reassignment overwrites the state class field's
    // defaults entirely, so this.state.country/paymentModel are actually undefined until the
    // user interacts - mirror the same `|| "AU"` / `|| "Keep 100"` fallbacks used elsewhere in
    // this render (CountrySelector's `selected`, ButtonSelect's `selectedValue`) so this checks
    // the effective, currently-displayed country/model, not the possibly-undefined raw state.
    const effectiveCountry = this.state.country || "AU";
    // Australia and EU countries never offer Keep100 - hide the button entirely, the same
    // way EU/UK already did before this ticket.
    const hideKeep100Option = defaultsToSharedModel(effectiveCountry);
    const effectivePaymentModel = this.state.paymentModel || (hideKeep100Option ? "Shared" : "Keep 100");

    return (
      <div className="fees">
        <div className="fees__pricing-model">
          <h1 className="text-heading --size-2">Choose your pricing model</h1>

          <CountrySelector
            selected={this.state.country ? this.state.country : "AU"}
            onChange={this.onCountryChange}
            countries={this.props.countryFees || this.state.countryFees}
          />

          <InputGroup label="">
            <ButtonSelect
              onChange={this.onPaymentFeeChange}
              defaultOption="Keep 100"
              buttons={hideKeep100Option ? paymentModelButtons.slice(1) : paymentModelButtons}
              options={null}
              selectedValue={effectivePaymentModel}
              isRounded
            />
          </InputGroup>

          <div className="fees__donation-form">
            <FeeStructureDonationForm
              feeStructure={this.state.selectedCountryFee}
              paymentModel={effectivePaymentModel}
            />
          </div>

          <p className="fees__pricing-small-text">
            {
              effectivePaymentModel === "Keep 100"
                ? "*Based on domestic credit and debit cards. Fees for American Express, PayPal and international cards are slightly higher."
                : "*Includes domestic and international cards, American Express and PayPal"
            }
          </p>
          <p className="fees__pricing-small-text">
            *PayPal fees will vary based on the campaigner’s PayPal account
          </p>
          <p className="fees__pricing-small-text">
            *Donations made in a foreign currency may be charged different rates
          </p>
        </div>

        <div className="fees__common-questions">
          <h1 className="text-heading --size-3">Common Questions</h1>
          <Accordion>
            <AccordionElement
              id="why_optional"
              title_text="Why optional donations?"
              open={false}
            >
              <h2>For Campaigners</h2>
              <div className="viewpoints">
                <div className="viewpoint full-width">
                  <div className="full-width">
                    <IconKeepWhatYouRaise />
                  </div>
                  <div className="text-block">
                    <h3>So you can keep all you raise</h3>
                    <p>100% of the chosen donated amount goes directly to you.</p>
                  </div>
                  <hr />
                </div>
                <div className="viewpoint half-width">
                  <div className="full-width">
                    <IconPracticeWhatYouPreach />
                  </div>
                  <div className="text-block">
                    <h3>To practice what we preach</h3>
                    <p>We use crowdfunding to fund our work, just like you do.</p>
                  </div>
                  <hr />
                </div>
                <div className="viewpoint half-width">
                  <div className="full-width">
                    <IconGiveDonorsAChoice />
                  </div>
                  <div className="text-block">
                    <h3>To give the donor a choice</h3>
                    <p>If you believe in transparency too, you can support us.</p>
                  </div>
                  <hr />
                </div>
                <div className="viewpoint half-width">
                  <div className="full-width">
                    <IconKeepUsHonest />
                  </div>
                  <div className="text-block">
                    <h3>To keep us honest</h3>
                    <p>Donors only give to us if they get a great experience, so we aim to give them one.</p>
                  </div>
                  <hr />
                </div>
                <div className="viewpoint half-width">
                  <div className="full-width">
                    <IconStayFocused />
                  </div>
                  <div className="text-block">
                    <h3>So we stay focused</h3>
                    <p>We're here to help you take action on social causes, not to make money from your campaign.</p>
                  </div>
                  <hr />
                </div>
              </div>
            </AccordionElement>
            <AccordionElement
              id="how_funded"
              title_text="How are we funded?"
              open={false}
            >
              <h2>Funding</h2>
              <ContentSwitcher buttonsBelow>
                <ContentSwitcherElement id="now" button_text="Now" visible>
                  <GraphFundingNow />
                </ContentSwitcherElement>
                <ContentSwitcherElement id="future" button_text="Future" visible={false}>
                  <GraphFundingFuture />
                </ContentSwitcherElement>
              </ContentSwitcher>
              <div className="funding-types">
                <div className="funding-type funding-optional">
                  <div className="disc" />
                  <div className="text">Optional Donations</div>
                </div>
                <div className="funding-type funding-investment">
                  <div className="disc" />
                  <div className="text">Investment</div>
                </div>
                <div className="funding-type funding-philanthropy">
                  <div className="disc" />
                  <div className="text">Philanthropic and Government Grants</div>
                </div>
              </div>

              <h2>Vision</h2>
              <div className="text-block">
                <h3>Fairer world</h3>
                <p>We envisage a future where everyone has access to a wider pool of funds to get their ideas going.</p>
              </div>
              <div className="text-block">
                <h3>Trusted connections</h3>
                <p>We're working hard on building a platform that facilitates relationships and enables openness.</p>
              </div>
              <div className="text-block">
                <h3>Funded by the people</h3>
                <p>By championing our Optional Donation model we trust that you like what we are doing.</p>
              </div>
            </AccordionElement>
          </Accordion>
        </div>
      </div>
    );
  }
}

export default Fees;
